import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, Pencil, Users, Shield, User, HardHat, UserCheck, Building2, Check, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog per assegnazione cantieri
  const [isSitesDialogOpen, setIsSitesDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);
  const [savingSites, setSavingSites] = useState(false);
  
  // Dialog per conversione worker -> operative
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [convertingEmployee, setConvertingEmployee] = useState(null);
  const [convertFormData, setConvertFormData] = useState({
    email: '',
    password: '',
    role: 'employee',
    can_create_drafts: false
  });
  const [converting, setConverting] = useState(false);
  
  // Form for operatives (with access)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    matricola: '',
    role: 'employee',
    employee_type: 'operative',
    can_create_drafts: false,
    can_correct_reports: false
  });
  
  // Form for workers (without access)
  const [workerFormData, setWorkerFormData] = useState({
    name: '',
    matricola: ''
  });
  
  const [isWorkerDialogOpen, setIsWorkerDialogOpen] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchSites();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      // I dipendenti arrivano già ordinati dal backend per nome (COGNOME NOME)
      setEmployees(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dipendenti');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      // Usa endpoint /sites/all per admin/gestore (tutti i cantieri)
      const response = await axios.get(`${API}/sites/all`);
      setSites(response.data.sort((a, b) => a.name.localeCompare(b.name, 'it')));
    } catch (error) {
      // Fallback a endpoint normale
      try {
        const response = await axios.get(`${API}/sites`);
        setSites(response.data.sort((a, b) => a.name.localeCompare(b.name, 'it')));
      } catch (err) {
        console.error('Errore caricamento cantieri:', err);
      }
    }
  };

  // Apri dialog assegnazione cantieri
  const handleOpenSitesDialog = (employee) => {
    setSelectedEmployee(employee);
    setSelectedSites(employee.assigned_sites || []);
    setIsSitesDialogOpen(true);
  };

  // Toggle selezione cantiere
  const toggleSiteSelection = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Seleziona/deseleziona tutti
  const toggleSelectAll = () => {
    if (selectedSites.length === sites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sites.map(s => s.id));
    }
  };

  // Salva assegnazione cantieri
  const handleSaveSites = async () => {
    if (!selectedEmployee) return;
    setSavingSites(true);
    try {
      await axios.put(`${API}/employees/${selectedEmployee.id}/assigned-sites`, {
        assigned_sites: selectedSites
      });
      toast.success('Cantieri assegnati con successo!');
      setIsSitesDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingSites(false);
    }
  };

  // Submit for operatives (with access)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        const { password, ...updateData } = formData;
        await axios.put(`${API}/employees/${editingEmployee.id}`, updateData);
        toast.success('Dipendente aggiornato!');
      } else {
        await axios.post(`${API}/auth/register`, formData);
        toast.success('Operativo creato! Email di benvenuto inviata.');
      }
      setIsDialogOpen(false);
      setEditingEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };
  
  // Submit for workers (without access)
  const handleWorkerSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/workers`, workerFormData);
      toast.success('Lavoratore creato!');
      setIsWorkerDialogOpen(false);
      resetWorkerForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      matricola: '',
      role: 'employee',
      employee_type: 'operative'
    });
  };
  
  const resetWorkerForm = () => {
    setWorkerFormData({
      name: '',
      matricola: ''
    });
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    if (employee.employee_type === 'worker' || employee.role === 'worker') {
      // Cannot edit workers through operative form
      toast.error('I lavoratori possono essere solo eliminati');
      return;
    }
    setFormData({
      email: employee.email || '',
      password: '',
      name: employee.name,
      matricola: employee.matricola,
      role: employee.role,
      employee_type: employee.employee_type || 'operative',
      can_create_drafts: employee.can_create_drafts || false,
      can_correct_reports: employee.can_correct_reports || false
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo dipendente?')) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Dipendente eliminato!');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Apri dialog conversione worker -> operative
  const openConvertDialog = (employee) => {
    setConvertingEmployee(employee);
    setConvertFormData({
      email: '',
      password: '',
      role: 'employee',
      can_create_drafts: false,
      can_correct_reports: false
    });
    setIsConvertDialogOpen(true);
  };

  // Converti worker in operative
  const handleConvert = async (e) => {
    e.preventDefault();
    if (!convertFormData.email || !convertFormData.password) {
      toast.error('Email e password sono obbligatori');
      return;
    }
    if (convertFormData.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setConverting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${convertingEmployee.id}/convert-to-operative`,
        null,
        {
          params: {
            email: convertFormData.email,
            password: convertFormData.password,
            role: convertFormData.role,
            can_create_drafts: convertFormData.can_create_drafts,
            can_correct_reports: convertFormData.can_correct_reports
          }
        }
      );
      toast.success(response.data.message);
      setIsConvertDialogOpen(false);
      setConvertingEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conversione');
    } finally {
      setConverting(false);
    }
  };

  // Filter employees based on tab
  const filteredEmployees = employees.filter(emp => {
    const isWorker = emp.employee_type === 'worker' || emp.role === 'worker';
    if (activeTab === 'all') return true;
    if (activeTab === 'operatives') return !isWorker;
    if (activeTab === 'workers') return isWorker;
    return true;
  });

  // Get role display name and colors
  const getRoleInfo = (employee) => {
    const isWorker = employee.employee_type === 'worker' || employee.role === 'worker';
    if (isWorker) {
      return { label: 'Lavoratore', bgColor: 'bg-amber-100', textColor: 'text-amber-700', icon: HardHat, iconColor: 'text-amber-600', iconBg: 'bg-amber-100' };
    }
    switch (employee.role) {
      case 'admin':
        return { label: 'Admin', bgColor: 'bg-purple-100', textColor: 'text-purple-700', icon: Shield, iconColor: 'text-purple-600', iconBg: 'bg-purple-100' };
      case 'gestore':
        return { label: 'Gestore', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', icon: UserCheck, iconColor: 'text-emerald-600', iconBg: 'bg-emerald-100' };
      default:
        return { label: 'Dipendente', bgColor: 'bg-slate-100', textColor: 'text-slate-600', icon: User, iconColor: 'text-[#1B6B93]', iconBg: 'bg-[#A8D8EA]' };
    }
  };

  // Count by type
  const operativesCount = employees.filter(e => e.employee_type !== 'worker' && e.role !== 'worker').length;
  const workersCount = employees.filter(e => e.employee_type === 'worker' || e.role === 'worker').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Dipendenti</h1>
          <p className="text-slate-500 mt-1">
            {operativesCount} operativi, {workersCount} lavoratori
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Dialog for Workers (without access) */}
          <Dialog open={isWorkerDialogOpen} onOpenChange={(open) => {
            setIsWorkerDialogOpen(open);
            if (!open) resetWorkerForm();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="new-worker-btn">
                <HardHat className="w-4 h-4 mr-2" />
                Nuovo Lavoratore
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-amber-600 flex items-center gap-2">
                  <HardHat className="w-5 h-5" /> Nuovo Lavoratore
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 mb-4">
                I lavoratori sono dipendenti senza accesso al sistema. Vengono utilizzati solo per l'assegnazione ai rapporti di lavoro.
              </p>
              <form onSubmit={handleWorkerSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Completo *</Label>
                  <Input
                    value={workerFormData.name}
                    onChange={(e) => setWorkerFormData({ ...workerFormData, name: e.target.value })}
                    placeholder="Mario Rossi"
                    required
                    className="input-field bg-white"
                    data-testid="worker-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Matricola *</Label>
                  <Input
                    value={workerFormData.matricola}
                    onChange={(e) => setWorkerFormData({ ...workerFormData, matricola: e.target.value })}
                    placeholder="LAV001"
                    required
                    className="input-field bg-white font-mono"
                    data-testid="worker-matricola-input"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsWorkerDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="worker-submit-btn">
                    Crea Lavoratore
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Dialog for Operatives (with access) */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingEmployee(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="new-employee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Operativo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-[#1B6B93] flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  {editingEmployee ? 'Modifica Operativo' : 'Nuovo Operativo'}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 mb-4">
                Gli operativi hanno accesso al sistema con email e password.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Completo *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Mario Rossi"
                    required
                    className="input-field bg-white"
                    data-testid="employee-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Matricola *</Label>
                    <Input
                      value={formData.matricola}
                      onChange={(e) => setFormData({ ...formData, matricola: e.target.value })}
                      placeholder="DIP001"
                      required
                      className="input-field bg-white font-mono"
                      data-testid="employee-matricola-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ruolo *</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                      <SelectTrigger className="input-field bg-white" data-testid="employee-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Dipendente</SelectItem>
                        <SelectItem value="gestore">Gestore</SelectItem>
                        <SelectItem value="admin">Amministratore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mario.rossi@splendorsud.it"
                    required
                    className="input-field bg-white"
                    data-testid="employee-email-input"
                  />
                </div>
                
                {!editingEmployee && (
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="input-field bg-white"
                      data-testid="employee-password-input"
                    />
                  </div>
                )}
                
                {/* Opzione Bozze */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <Label className="text-sm font-medium">Abilita creazione bozze</Label>
                    <p className="text-xs text-slate-500">Permette di salvare rapporti come bozze da completare poi</p>
                  </div>
                  <Switch
                    checked={formData.can_create_drafts || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_create_drafts: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <div>
                    <Label className="text-sm font-medium">Abilita correzione rapporti</Label>
                    <p className="text-xs text-slate-500">Permette di correggere i propri rapporti approvati (rigenera PDF e reinvia email)</p>
                  </div>
                  <Switch
                    checked={formData.can_correct_reports || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_correct_reports: checked })}
                    data-testid="can-correct-reports-toggle"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="employee-submit-btn">
                    {editingEmployee ? 'Aggiorna' : 'Crea Operativo'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs for filtering */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1B6B93] data-[state=active]:text-white">
            Tutti ({employees.length})
          </TabsTrigger>
          <TabsTrigger value="operatives" className="data-[state=active]:bg-[#1B6B93] data-[state=active]:text-white">
            Operativi ({operativesCount})
          </TabsTrigger>
          <TabsTrigger value="workers" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
            Lavoratori ({workersCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Employees Grid */}
      {filteredEmployees.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {activeTab === 'workers' ? 'Nessun lavoratore registrato' : 
               activeTab === 'operatives' ? 'Nessun operativo registrato' : 
               'Nessun dipendente registrato'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {filteredEmployees.map((employee) => {
            const roleInfo = getRoleInfo(employee);
            const IconComponent = roleInfo.icon;
            const isWorker = employee.employee_type === 'worker' || employee.role === 'worker';
            
            return (
              <Card key={employee.id} className="shadow-sm hover:shadow-md transition-shadow animate-fade-in" data-testid={`employee-card-${employee.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleInfo.iconBg}`}>
                        <IconComponent className={`w-6 h-6 ${roleInfo.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{employee.name}</h3>
                        <p className="text-sm text-slate-500">
                          {isWorker ? (
                            <span className="italic text-amber-600">Solo anagrafica</span>
                          ) : (
                            employee.email
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400">Matricola</p>
                      <p className="font-mono font-semibold text-[#1B6B93]">{employee.matricola}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleInfo.bgColor} ${roleInfo.textColor}`}>
                      {roleInfo.label}
                    </span>
                  </div>
                  
                  <div className="mt-4 flex justify-end gap-2">
                    {/* Pulsante assegnazione cantieri - solo per operativi (non admin/gestore) */}
                    {!isWorker && employee.role === 'employee' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:bg-emerald-100"
                        onClick={() => handleOpenSitesDialog(employee)}
                        data-testid={`assign-sites-btn-${employee.id}`}
                        title="Assegna cantieri"
                      >
                        <Building2 className="w-4 h-4" />
                        {employee.assigned_sites?.length > 0 && (
                          <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                            {employee.assigned_sites.length}
                          </span>
                        )}
                      </Button>
                    )}
                    {!isWorker && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(employee)}
                        data-testid={`edit-employee-btn-${employee.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Pulsante per convertire worker in operative */}
                    {isWorker && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:bg-emerald-100"
                        onClick={() => openConvertDialog(employee)}
                        title="Converti in Operativo"
                        data-testid={`convert-worker-btn-${employee.id}`}
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-100"
                      onClick={() => handleDelete(employee.id)}
                      data-testid={`delete-employee-btn-${employee.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Conversione Worker -> Operative */}
      <Dialog open={isConvertDialogOpen} onOpenChange={(open) => {
        setIsConvertDialogOpen(open);
        if (!open) setConvertingEmployee(null);
      }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-emerald-600 flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Converti in Operativo
            </DialogTitle>
          </DialogHeader>
          
          {convertingEmployee && (
            <form onSubmit={handleConvert} className="space-y-4">
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">Stai convertendo:</p>
                <p className="font-semibold text-amber-900">{convertingEmployee.name}</p>
                <p className="text-xs text-amber-600">Matricola: {convertingEmployee.matricola}</p>
              </div>
              
              <p className="text-sm text-slate-600">
                Inserisci le credenziali per permettere a questo dipendente di accedere al sistema.
              </p>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={convertFormData.email}
                  onChange={(e) => setConvertFormData({ ...convertFormData, email: e.target.value })}
                  placeholder="email@esempio.com"
                  required
                  className="input-field bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="text"
                  value={convertFormData.password}
                  onChange={(e) => setConvertFormData({ ...convertFormData, password: e.target.value })}
                  placeholder="Minimo 6 caratteri"
                  required
                  minLength={6}
                  className="input-field bg-white"
                />
                <p className="text-xs text-slate-500">La password sarà inviata via email al dipendente</p>
              </div>
              
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select 
                  value={convertFormData.role} 
                  onValueChange={(value) => setConvertFormData({ ...convertFormData, role: value })}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Dipendente Operativo</SelectItem>
                    <SelectItem value="gestore">Gestore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Può creare bozze rapporti</Label>
                  <p className="text-xs text-slate-500">Permette di salvare rapporti come bozze</p>
                </div>
                <Switch 
                  checked={convertFormData.can_create_drafts}
                  onCheckedChange={(checked) => setConvertFormData({ ...convertFormData, can_create_drafts: checked })}
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Può correggere rapporti</Label>
                  <p className="text-xs text-slate-500">Permette di correggere i propri rapporti approvati</p>
                </div>
                <Switch 
                  checked={convertFormData.can_correct_reports || false}
                  onCheckedChange={(checked) => setConvertFormData({ ...convertFormData, can_correct_reports: checked })}
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsConvertDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" disabled={converting} className="bg-emerald-600 hover:bg-emerald-700">
                  {converting ? 'Conversione...' : 'Converti in Operativo'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Assegnazione Cantieri */}
      <Dialog open={isSitesDialogOpen} onOpenChange={setIsSitesDialogOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-emerald-600 flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Assegna Cantieri
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Dipendente:</p>
                <p className="font-semibold text-slate-800">{selectedEmployee.name}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {selectedSites.length === 0 
                    ? 'Nessuna restrizione (vede tutti i cantieri)' 
                    : `${selectedSites.length} cantier${selectedSites.length === 1 ? 'e' : 'i'} selezionat${selectedSites.length === 1 ? 'o' : 'i'}`}
                </p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={toggleSelectAll}
                >
                  {selectedSites.length === sites.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-y-auto flex-1 max-h-[40vh]">
                {sites.map((site) => (
                  <div 
                    key={site.id}
                    onClick={() => toggleSiteSelection(site.id)}
                    className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
                      selectedSites.includes(site.id) ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedSites.includes(site.id) 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-slate-300'
                    }`}>
                      {selectedSites.includes(site.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{site.name}</p>
                      <p className="text-xs text-slate-500 truncate">{site.client_name || site.address}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-amber-50 p-3 rounded-lg text-xs text-amber-700">
                <strong>Nota:</strong> Se non selezioni nessun cantiere, il dipendente potrà vedere tutti i cantieri.
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsSitesDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleSaveSites} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={savingSites}
                >
                  {savingSites ? 'Salvataggio...' : 'Salva Assegnazione'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
